import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { fetchPosts, createPost, updatePost, deletePost, uploadBlogImage, deleteComment } from '../../api';

export default function ManageBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPostId, setEditingPostId] = useState(null);
  const [formData, setFormData] = useState({ 
    title: '', 
    content: '',
    titleFr: '',
    contentFr: '',
    published: true
  });
  const [activeLang, setActiveLang] = useState('en');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    loadPosts();
  }, []);

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        const formData = new FormData();
        formData.append('image', file);
        try {
          const res = await uploadBlogImage(formData);
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection();
          quill.insertEmbed(range.index, 'image', res.url);
        } catch (error) {
          console.error('Image upload failed', error);
          alert('Failed to upload image to Cloudflare');
        }
      }
    };
  };

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link', 'image', 'blockquote', 'code-block'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), []);

  const loadPosts = async () => {
    try {
      const data = await fetchPosts(true);
      setPosts(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const drafts = useMemo(() => posts.filter(p => !p.published), [posts]);
  const publishedPosts = useMemo(() => posts.filter(p => p.published), [posts]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContentChange = (content) => {
    if (activeLang === 'en') {
      setFormData({ ...formData, content });
    } else {
      setFormData({ ...formData, contentFr: content });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('content', formData.content);
      data.append('titleFr', formData.titleFr);
      data.append('contentFr', formData.contentFr);
      data.append('published', formData.published);
      if (imageFile) data.append('image', imageFile);

      if (editingPostId) {
        await updatePost(editingPostId, data);
      } else {
        await createPost(data);
      }
      
      resetForm();
      loadPosts();
      alert('Article saved successfully!');
    } catch (error) {
      console.error(error);
      alert('Failed to save article');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', titleFr: '', contentFr: '', published: true });
    setImageFile(null);
    setImagePreview(null);
    setEditingPostId(null);
    setActiveLang('en');
  };

  const handleEditClick = (post) => {
    setEditingPostId(post.id);
    setFormData({ 
      title: post.title, 
      content: post.content,
      titleFr: post.titleFr || '',
      contentFr: post.contentFr || '',
      published: post.published
    });
    setImagePreview(post.coverImage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Delete this article?')) {
      try {
        await deletePost(id);
        loadPosts();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleDeleteComment = async (id) => {
    if (window.confirm('Delete this comment?')) {
      try {
        await deleteComment(id);
        loadPosts();
      } catch (error) {
        console.error(error);
        alert('Failed to delete comment');
      }
    }
  };



  if (loading) return <div className="text-gray-400 text-center py-10">Loading blog...</div>;

  return (
    <div className="flex flex-col gap-10 text-[#f1edfd]">
      <div className="bg-[#1a1a1c] border border-gray-800 p-6 lg:p-8 rounded-3xl shadow-xl">
        <h2 className="text-2xl font-Poppins font-bold mb-8 text-[#3aa1ff]">
          {editingPostId ? 'Edit Article' : 'Write New Article'}
        </h2>
        
        <div className="flex gap-4 mb-8 border-b border-gray-800 pb-4">
          <button
            onClick={() => setActiveLang('en')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${activeLang === 'en' ? 'bg-[#3aa1ff] text-[#131315]' : 'bg-[#131315] text-gray-400 hover:text-white'}`}
          >
            English Version
          </button>
          <button
            onClick={() => setActiveLang('fr')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${activeLang === 'fr' ? 'bg-[#3aa1ff] text-[#131315]' : 'bg-[#131315] text-gray-400 hover:text-white'}`}
          >
            Version Française
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-400">
              Article Title ({activeLang.toUpperCase()})
            </label>
            <input
              type="text"
              name={activeLang === 'en' ? 'title' : 'titleFr'}
              value={activeLang === 'en' ? formData.title : formData.titleFr}
              onChange={handleInputChange}
              required={activeLang === 'en'}
              placeholder={activeLang === 'en' ? "Enter a catchy title..." : "Entrez un titre accrocheur..."}
              className="bg-[#131315] border border-gray-800 rounded-xl px-4 py-4 text-lg font-Poppins focus:outline-none focus:border-[#3aa1ff] transition-all"
            />
          </div>

          {activeLang === 'en' && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-400">Cover Image (Shared)</label>
              <div className="flex items-center gap-6 p-4 bg-[#131315] border border-gray-800 border-dashed rounded-xl">
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="w-24 h-24 object-cover rounded-lg shadow-lg border border-gray-700" />
                )}
                <input type="file" onChange={handleFileChange} className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-[#3aa1ff]/10 file:text-[#3aa1ff] hover:file:bg-[#3aa1ff]/20" />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 min-h-[400px]">
            <label className="text-sm font-medium text-gray-400">
              Content ({activeLang.toUpperCase()})
            </label>
            <div className="bg-[#131315] rounded-xl overflow-hidden border border-gray-800">
              <ReactQuill 
                ref={quillRef}
                theme="snow"
                value={activeLang === 'en' ? formData.content : formData.contentFr}
                onChange={handleContentChange}
                modules={quillModules}
                className="text-[#f1edfd]"
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 p-4 bg-[#131315] border border-gray-800 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-400">Post Visibility</label>
                <p className="text-[10px] text-gray-600 uppercase tracking-widest font-bold">
                  {formData.published ? 'Article will be live for everyone' : 'Article will only be visible to you'}
                </p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer group">
                <span className={`font-Poppins text-sm ${!formData.published ? 'text-yellow-500 font-bold' : 'text-gray-400 group-hover:text-gray-200'}`}>Save as Draft</span>
                <div className="relative inline-block w-12 h-6 transition duration-200 ease-in">
                  <input 
                    type="checkbox" 
                    name="published" 
                    checked={!formData.published}
                    onChange={(e) => setFormData({...formData, published: !e.target.checked})}
                    className="absolute block w-6 h-6 bg-white border-4 rounded-full appearance-none cursor-pointer checked:right-0 right-6 transition-all duration-200 border-gray-800 checked:bg-yellow-500"
                  />
                  <div className={`block h-6 overflow-hidden rounded-full cursor-pointer transition-colors ${!formData.published ? 'bg-yellow-500/20' : 'bg-gray-800'}`}></div>
                </div>
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 px-8 py-4 rounded-xl font-Poppins font-bold transition-all disabled:opacity-50 ${formData.published ? 'bg-[#3aa1ff] text-[#131315] hover:bg-[#288ce6]' : 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 border border-yellow-500/30'}`}
            >
              {submitting ? 'Saving...' : editingPostId ? 'Update Article' : formData.published ? 'Publish Article' : 'Save as Draft'}
            </button>
            {editingPostId && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-800 text-gray-400 px-8 py-4 rounded-xl font-Poppins font-bold hover:bg-gray-700 hover:text-white transition-all"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Drafts Section */}
      {drafts.length > 0 && (
        <div className="bg-[#1a1a1c] border border-gray-800 p-6 lg:p-8 rounded-3xl shadow-xl">
          <h2 className="text-2xl font-Poppins font-bold mb-8 text-yellow-500 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></span>
            Drafts ({drafts.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {drafts.map(post => (
              <div key={post.id} className="bg-[#131315] border border-gray-800 border-l-4 border-l-yellow-500 rounded-2xl overflow-hidden hover:border-yellow-500/50 transition-all group">
                <div className="p-6">
                  <h3 className="font-bold text-lg mb-4 line-clamp-2 text-gray-200">{post.title}</h3>
                  <div className="flex justify-between items-center mt-6">
                    <div className="flex gap-3">
                      <button onClick={() => handleEditClick(post)} className="text-[#3aa1ff] hover:underline text-sm font-bold">Edit / Publish</button>
                      <button onClick={() => handleDeleteClick(post.id)} className="text-red-400 hover:underline text-sm font-bold">Delete</button>
                    </div>
                    <span className="text-gray-600 text-xs">{new Date(post.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#1a1a1c] border border-gray-800 p-6 lg:p-8 rounded-3xl shadow-xl">
        <h2 className="text-2xl font-Poppins font-bold mb-8 flex items-center gap-3">
          Published Articles ({publishedPosts.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {publishedPosts.map(post => (
            <div key={post.id} className="bg-[#131315] border border-gray-800 rounded-2xl overflow-hidden hover:border-[#3aa1ff]/50 transition-all group">
              {post.coverImage && (
                <img src={post.coverImage} alt={post.title} className="w-full h-40 object-cover opacity-80 group-hover:opacity-100 transition-all" />
              )}
              <div className="p-6">
                <h3 className="font-bold text-xl mb-4 line-clamp-2">{post.title}</h3>
                
                {/* Comments List for Admin */}
                {post.comments && post.comments.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-800 flex flex-col gap-3 max-h-40 overflow-y-auto">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Recent Comments</p>
                    {post.comments.map(comment => (
                      <div key={comment.id} className="flex justify-between items-start gap-2 bg-[#1a1a1c] p-2 rounded-lg">
                        <div className="flex flex-col gap-1 overflow-hidden">
                          <span className="text-[10px] text-[#3aa1ff] font-bold">{comment.name}</span>
                          <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">{comment.content}</p>
                        </div>
                        <button 
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-red-500 hover:text-red-400 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-between items-center mt-6">
                  <div className="flex gap-3">
                    <button onClick={() => handleEditClick(post)} className="text-[#3aa1ff] hover:underline text-sm font-bold">Edit</button>
                    <button onClick={() => handleDeleteClick(post.id)} className="text-red-400 hover:underline text-sm font-bold">Delete</button>
                  </div>
                  <span className="text-gray-600 text-xs">{new Date(post.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
          {publishedPosts.length === 0 && <p className="text-gray-500 col-span-2 text-center py-10 italic">No articles published yet.</p>}
        </div>
      </div>
    </div>
  );
}
